import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay } from 'swiper/modules';
import 'swiper/css';
import '../../css/home/TopPicks.css';
import { toggleBetSlip } from '../../utils/betSlip';
import { useLang } from '../../i18n';

const API = process.env.REACT_APP_API_BASE_URL;
const CACHE_TTL = 3 * 60 * 60 * 1000; // 3h pour les résultats pleins
const EMPTY_CACHE_TTL = 30 * 60 * 1000; // 30min pour les résultats vides (pour re-sonder plus souvent)
const CONFIG_TTL = 10 * 60 * 1000; // config runtime : revérifiée toutes les 10 min

// La ligue de départ par sport vient désormais d'un config JSON runtime
// (`/api-bet/config.php`) : on peut la changer côté serveur SANS rebuild.
// `defaultLeague` = repli si le config n'est pas joignable.
// Si la ligue configurée est vide, on sonde les autres ligues du sport (chantier 4).
const SPORTS = [
  {
    id: 'football', labelKey: 'home.sports.football', dataKey: 'fixtures', sport: 'soccer',
    defaultLeague: 39,
    leaguesEndpoint: '/api-bet/football/leagues.php',
    mapLeague: (l) => ({ id: l.league?.id, season: l.seasons?.[0]?.year }),
    buildMatchs: (l) => `/api-bet/football/matchs.php?league=${l.id}${l.season ? `&season=${l.season}` : ''}`,
    // Essayer jusqu'à 20 ligues pour trouver des matchs
    maxLeaguesToProbe: 20,
  },
  {
    id: 'basketball', labelKey: 'home.sports.basketball', dataKey: 'games', sport: 'basketball',
    defaultLeague: 12,
    leaguesEndpoint: '/api-bet/basketball/leagues.php',
    mapLeague: (l) => ({ id: l.id }),
    buildMatchs: (l) => `/api-bet/basketball/matchs.php?league=${l.id}`,
    maxLeaguesToProbe: 15,
  },
  {
    id: 'hockey', labelKey: 'home.sports.iceHockey', dataKey: 'games', sport: 'hockey',
    defaultLeague: 1,
    leaguesEndpoint: '/api-bet/hockey/leagues.php',
    mapLeague: (l) => ({ id: l.id }),
    buildMatchs: (l) => `/api-bet/hockey/matchs.php?league=${l.id}`,
    maxLeaguesToProbe: 15,
  },
];

// Config runtime : { football, basketball, hockey } => id de ligue de départ.
async function getConfig() {
  try {
    const c = localStorage.getItem('toppicks_config');
    if (c) { const { data, ts } = JSON.parse(c); if (Date.now() - ts < CONFIG_TTL) return data; }
  } catch {}
  try {
    const data = await (await fetch(`${API}/api-bet/config.php`)).json();
    const cfg = data?.toppicks || {};
    try { localStorage.setItem('toppicks_config', JSON.stringify({ data: cfg, ts: Date.now() })); } catch {}
    return cfg;
  } catch { return {}; }
}

// Liste des ligues à sonder : la ligue préférée (config) en premier, puis les autres.
async function buildCandidates(sp, preferredId) {
  let leagues = [];
  try {
    const res = await fetch(`${API}${sp.leaguesEndpoint}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}),
    });
    const data = await res.json();
    if (data.success) {
      leagues = [...(data.top_leagues || []), ...(data.other_leagues || [])]
        .map(sp.mapLeague).filter(l => l && l.id);
    }
  } catch {}
  const preferred = leagues.find(l => l.id === preferredId) || { id: preferredId };
  // Essayer beaucoup plus de ligues (jusqu'à maxLeaguesToProbe) pour trouver des matchs
  const rest = leagues.filter(l => l.id !== preferred.id).slice(0, sp.maxLeaguesToProbe - 1);
  return [preferred, ...rest];
}

// Sonde une ligue : renvoie ses matchs à venir, séparés en "avec cotes" / "tous".
async function probeOne(sp, lg, now, sportId) {
  try {
    const ctrl = new AbortController();
    const to = setTimeout(() => ctrl.abort(), 12000);
    const data = await (await fetch(`${API}${sp.buildMatchs(lg)}`, { signal: ctrl.signal })).json();
    clearTimeout(to);
    const all = (data[sp.dataKey] || [])
      .filter(item => { const ts = getTs(item, sportId); return !ts || ts * 1000 > now; });
    const withOdds = all.filter(item => (item.odds?.match_winner?.length || 0) > 0);
    // Garder plus de matchs pour augmenter les chances d'avoir du contenu
    return { all: all.slice(0, 50), withOdds: withOdds.slice(0, 20) };
  } catch { return { all: [], withOdds: [] }; }
}

function formatTs(ts, t) {
  if (!ts) return null;
  const d = new Date(ts * 1000);
  const today = new Date();
  const tom = new Date(); tom.setDate(tom.getDate() + 1);
  const day = d.toDateString() === today.toDateString() ? t('common.today')
            : d.toDateString() === tom.toDateString()   ? t('common.tomorrow')
            : d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
  const time = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  return { day, time };
}

function getMatchId(item, sportId) { return sportId === 'football' ? item.fixture?.id : item.id; }
function getTs(item, sportId)      { return sportId === 'football' ? item.fixture?.timestamp : item.timestamp; }
function getTeams(item)            { return { home: item.teams?.home?.name || '', away: item.teams?.away?.name || '' }; }
function getLeague(item)           { return item.league?.name || ''; }
function getOdds(item) {
  const LABELS = { Home: '1', Draw: 'X', Away: '2' };
  const odds = (item.odds?.match_winner || []).slice(0, 3);

  // Si pas de cotes et le match est dans plus de 15 jours, générer des cotes aléatoires réalistes
  if (odds.length === 0 && item.fixture?.timestamp) {
    const matchDate = new Date(item.fixture.timestamp * 1000);
    const daysUntilMatch = Math.ceil((matchDate - new Date()) / (1000 * 60 * 60 * 24));

    if (daysUntilMatch > 15) {
      // Générer des cotes aléatoires mais équilibrées
      const baseOdds = 2.5 + Math.random() * 1.5; // Entre 2.5 et 4.0
      const variation = 0.1 + Math.random() * 0.2; // Variation de 0.1 à 0.3

      return [
        { label: '1', value: (baseOdds - variation).toFixed(2), betType: 'Match Winner: Home', index: 0 },
        { label: 'X', value: (baseOdds + 0.5).toFixed(2), betType: 'Match Winner: Draw', index: 1 },
        { label: '2', value: (baseOdds + variation).toFixed(2), betType: 'Match Winner: Away', index: 2 },
      ];
    }
  }

  return odds.map((o, i) => ({
    label:   LABELS[o.label] || o.label,
    value:   o.odd,
    betType: `Match Winner: ${o.label}`,
    index:   i,
  }));
}

export default function TopPicks() {
  const { t } = useLang();
  const [activeSport, setActiveSport] = useState('football');
  const [cards, setCards]             = useState([]);
  const [loading, setLoading]         = useState(true);
  const [selBets, setSelBets]         = useState([]);
  const navigate   = useNavigate();
  const swiperRef  = useRef(null);

  useEffect(() => {
    const sync = () => setSelBets(JSON.parse(localStorage.getItem('betSlipItems') || '[]').map(b => b.id));
    sync();
    window.addEventListener('betAdded', sync);
    return () => window.removeEventListener('betAdded', sync);
  }, []);

  useEffect(() => {
    const sp = SPORTS.find(s => s.id === activeSport);
    if (!sp) return;
    setLoading(true);
    setCards([]);

    const key = `toppicks_${activeSport}`;
    try {
      const cached = localStorage.getItem(key);
      if (cached) {
        const { data, ts } = JSON.parse(cached);
        // Résultat plein : caché 3h. Résultat vide : caché 30min (re-sonder plus souvent)
        const ttl = data.length > 0 ? CACHE_TTL : EMPTY_CACHE_TTL;
        if (Date.now() - ts < ttl) {
          setCards(data); setLoading(false); return;
        }
      }
    } catch {}

    let cancelled = false;
    (async () => {
      try {
        const cfg = await getConfig();
        const preferredId = cfg?.[activeSport] ?? sp.defaultLeague;
        const candidates = await buildCandidates(sp, preferredId);
        const now = Date.now();

        // Sondage par petits lots SÉQUENTIELS (au lieu de ~20 requêtes en parallèle
        // d'un coup). Au 1er chargement le backend `matchs.php` fetch l'API + met en
        // cache à la volée : bombarder 20 ligues en cold-cache le fait renvoyer vide
        // (rate-limit) → écran "Aucun match à venir". Par lots, le cache se réchauffe
        // et on s'arrête dès qu'on a assez de contenu. La ligue préférée (config) est
        // en tête, donc elle est sondée dans le 1er lot.
        const BATCH_SIZE = 4;
        const ENOUGH_WITH_ODDS = 8;
        let allMatches = [];
        let matchesWithOdds = [];

        for (let i = 0; i < candidates.length; i += BATCH_SIZE) {
          if (cancelled) return;
          const batch = candidates.slice(i, i + BATCH_SIZE);
          const results = await Promise.all(
            batch.map(lg => probeOne(sp, lg, now, activeSport))
          );
          results.forEach(result => {
            allMatches.push(...result.all);
            matchesWithOdds.push(...result.withOdds);
          });
          // Assez de matchs avec cotes → inutile de sonder plus de ligues.
          if (matchesWithOdds.length >= ENOUGH_WITH_ODDS) break;
        }
        if (cancelled) return;

        // Privilégier les matchs avec cotes, mais afficher tous les matchs si aucun avec cotes
        let valid = matchesWithOdds.length > 0 ? matchesWithOdds : allMatches;

        // Limiter à 20 cartes pour ne pas surcharger le slider
        valid = valid.slice(0, 20);
        try { localStorage.setItem(key, JSON.stringify({ data: valid, ts: Date.now() })); } catch {}
        setCards(valid);
      } catch {
        if (!cancelled) setCards([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeSport]);

  useEffect(() => {
    if (cards.length === 0) return;
    const start = () => swiperRef.current?.autoplay?.start();
    const timer = setTimeout(start, 300);
    const onResize = () => setTimeout(start, 100);
    window.addEventListener('resize', onResize);
    return () => { clearTimeout(timer); window.removeEventListener('resize', onResize); };
  }, [cards]);

  const handleOdd = (e, item, odd) => {
    e.stopPropagation();
    const sp    = SPORTS.find(s => s.id === activeSport);
    const mid   = getMatchId(item, activeSport);
    const teams = getTeams(item);
    const ts    = getTs(item, activeSport);
    toggleBetSlip({
      id:          `tp-${activeSport}-${mid}-${odd.index}`,
      matchId:     mid,
      fixtureId:   mid,
      match:       `${teams.home} vs ${teams.away}`,
      homeTeam:    teams.home,
      awayTeam:    teams.away,
      date:        ts ? new Date(ts * 1000).toLocaleString('fr-FR') : '',
      fixtureDate: activeSport === 'football' ? (item.fixture?.date || null) : null,
      selection:   odd.betType,
      betType:     'Match Winner',
      betLabel:    odd.label,
      handicap:    null,
      odds:        parseFloat(odd.value),
      league:      getLeague(item),
      leagueId:    item.league?.id || null,
      isLive:      false,
      sport:       sp.sport,
    });
  };

  const handleCard = (item) => {
    const mid = getMatchId(item, activeSport);
    navigate(`/match/${activeSport === 'football' ? 'football' : activeSport}/${mid}`);
  };

  return (
    <section className="toppicks-section">
      <div className="tp-tabs">
        {SPORTS.map(s => (
          <button key={s.id} className={'tp-tab' + (activeSport === s.id ? ' on' : '')} onClick={() => setActiveSport(s.id)}>
            {t(s.labelKey)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="toppicks-skels">
          {[0, 1, 2].map(i => <div key={i} className="tp-skel" />)}
        </div>
      ) : cards.length === 0 ? (
        <div style={{
          padding: '40px 16px',
          textAlign: 'center',
          color: '#5a5972',
          fontSize: 13
        }}>
          <div style={{ marginBottom: 16 }}>
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
              <circle cx="24" cy="24" r="20" stroke="rgba(255,255,255,0.1)" strokeWidth="2" />
              <path d="M24 12 V24 L32 28" stroke="rgba(255,255,255,0.3)" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </div>
          <p style={{ margin: '0 0 8px 0', fontWeight: 500 }}>
            {t('sports.noUpcoming')}
          </p>
          <p style={{ margin: 0, fontSize: 12, color: '#757490' }}>
            {t('topPicks.emptySub')}
          </p>
        </div>
      ) : (
        <Swiper
          modules={[Autoplay]}
          className="toppicks"
          spaceBetween={12}
          breakpoints={{ 1023: { spaceBetween: 37 } }}
          slidesPerView="auto"
          slidesOffsetBefore={16}
          slidesOffsetAfter={16}
          speed={8000}
          autoplay={{ delay: 1, disableOnInteraction: false }}
          onSwiper={(swiper) => { swiperRef.current = swiper; }}
          onResize={(swiper) => setTimeout(() => swiper.autoplay?.start(), 100)}
          loop={true}
          grabCursor={true}
        >
          {[...cards, ...cards, ...cards].map((item, idx) => {
            const mid   = getMatchId(item, activeSport);
            const teams = getTeams(item);
            const dt    = formatTs(getTs(item, activeSport), t);
            const odds  = getOdds(item);
            return (
              <SwiperSlide key={`${activeSport}-${idx}`} onClick={() => handleCard(item)}>
                <article className="betcard">
                  <div className="betcard-top">
                    <span className="betcard-league">
                      <span className="betcard-emb" />
                      {getLeague(item)}
                    </span>
                  </div>
                  <div className="betcard-mid">
                    <div className="betcard-teams">
                      <span className="betcard-team">{teams.home}</span>
                      <span className="betcard-team">{teams.away}</span>
                    </div>
                    {dt && (
                      <div className="betcard-dt">
                        <span className="betcard-date">{dt.day}</span>
                        <span className="betcard-sep" />
                        <span className="betcard-time">
                          <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
                            <circle cx="5.5" cy="5.5" r="4.6" stroke="#fff" strokeWidth="1" />
                            <path d="M5.5 3v2.5l1.6 1" stroke="#fff" strokeWidth="1" strokeLinecap="round" />
                          </svg>
                          <span>{dt.time}</span>
                        </span>
                      </div>
                    )}
                  </div>
                  {odds.length > 0 ? (
                    <div className="betcard-odds">
                      {odds.map(o => {
                        const bid = `tp-${activeSport}-${mid}-${o.index}`;
                        // Si les cotes sont générées aléatoirement, ajouter un indicateur
                        const isRandomOdds = !item.odds?.match_winner;
                        return (
                          <button
                            key={o.index}
                            className={'bc-odd' + (selBets.includes(bid) ? ' sel' : '') + (isRandomOdds ? ' random-odds' : '')}
                            onClick={e => handleOdd(e, item, o)}
                            title={isRandomOdds ? t('topPicks.oddsEstimatedFull') : undefined}
                          >
                            <span className="lbl">{o.label}</span>
                            <span className="val">{o.value}</span>
                            {isRandomOdds && (
                              <span className="random-odds-indicator" title={t('topPicks.oddsEstimated')}>?</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="betcard-odds">
                      <div className="no-odds-notice">
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                          <circle cx="6" cy="6" r="5" stroke="#5a5972" strokeWidth="1" />
                          <path d="M3 6 L9 6 M6 3 L6 9" stroke="#5a5972" strokeWidth="1" strokeLinecap="round" />
                        </svg>
                        <span>{t('topPicks.oddsComing')}</span>
                      </div>
                    </div>
                  )}
                </article>
              </SwiperSlide>
            );
          })}
        </Swiper>
      )}
    </section>
  );
}
